import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage-angular';
import { Deferred } from 'src/app/common/deferred/deferred';
import { buildNumber, latestCommit } from 'src/environments/build-number';
import { environment } from 'src/environments/environment';

import { AppflowLiveUpdatesService } from '@services/appflow-live-updates/appflow-live-updates.service';
import { IAuthConfig, IEnvironmentAuthConfig } from 'src/environments/environment.default';
import makeDebug from '../../../../makeDebug';

const debug = makeDebug('services:environment');

export interface BranchUrls {
  dev: string;
  staging: string;
  production: string;
  uat: string;
  mdr: string;
}

export const Branch = {
  dev: 'dev',
  staging: 'staging',
  production: 'production',
  uat: 'uat',
  mdr: 'mdr',
};

export const OAuthConfig = new InjectionToken<IEnvironmentAuthConfig>('Environment OAuth Config', {
  providedIn: 'root',
  factory: () => environment.environmentSettings.authConfig,
});

export const AlbertaUrls = new InjectionToken<BranchUrls>('alberta baseUrls', {
  providedIn: 'root',
  factory: () => environment.environmentSettings.albertaUrl as BranchUrls,
});

export const SignalingServerUrls = new InjectionToken<BranchUrls>('signaling server urls', {
  providedIn: 'root',
  factory: () => environment.environmentSettings.signalingServer as BranchUrls,
});

export const PortalUrls = new InjectionToken<BranchUrls>('portal urls', {
  providedIn: 'root',
  factory: () => environment.environmentSettings.portalUrls as BranchUrls,
});

export const KimServiceUrls = new InjectionToken<BranchUrls>('kim service urls', {
  providedIn: 'root',
  factory: () => environment.environmentSettings.kimServiceUrl as BranchUrls,
});

export const KeaServiceUrls = new InjectionToken<BranchUrls>('kea service urls', {
  providedIn: 'root',
  factory: () => environment.environmentSettings.keaServiceUrl as BranchUrls,
});

export const EnvironmentBranch = new InjectionToken<string>('environment branch', {
  providedIn: 'root',
  factory: () => ((environment as any).branch ? (environment as any).branch : null),
});

export const WindowHost = new InjectionToken<string>('window host', {
  providedIn: 'root',
  factory: () => window.location.host.split(':')[0],
});

@Injectable({ providedIn: 'root' })
export class EnvironmentService {
  private _ready = new Deferred<void>();
  public get ready(): Promise<void> {
    return this._ready.promise;
  }

  public get version() {
    return environment.packageVersion;
  }

  private _buildNumber: string;
  public get buildNumber() {
    return this._buildNumber;
  }

  private _gitCommitHash: string;
  public get getCommitHash() {
    return this._gitCommitHash;
  }

  private _portalUrl: string;
  public get portalUrl() {
    return this._portalUrl;
  }

  private _signalingServerUrl: string;
  public get signalingServerUrl() {
    return this._signalingServerUrl;
  }

  private _branch: string;
  public get branch() {
    return this._branch;
  }

  private _isProduction = false;
  public get isProduction() {
    return this._isProduction;
  }

  private _alberta_baseurl: string;
  public get alberta_baseurl() {
    return this._alberta_baseurl;
  }

  private _kimServiceUrl: string;
  public get kimServiceUrl() {
    return this._kimServiceUrl;
  }

  private _keaServiceUrl: string;
  public get keaServiceUrl() {
    return this._keaServiceUrl;
  }

  private _authConfig: IAuthConfig;
  public get authConfig() {
    return this._authConfig;
  }

  constructor(
    private _platform: Platform,
    @Inject(AlbertaUrls)
    private _albertaBaseUrls: BranchUrls,
    @Inject(SignalingServerUrls) private readonly _signalingServerUrls: BranchUrls,
    @Inject(PortalUrls) private readonly _portalUrls: BranchUrls,
    @Inject(KimServiceUrls) private readonly _kimServiceUrls: BranchUrls,
    @Inject(KeaServiceUrls) private readonly _keaServiceUrls: BranchUrls,
    @Inject(EnvironmentBranch) private _environmentBranch: string,
    @Inject(WindowHost) private _windowHost: string,
    @Inject(OAuthConfig) private _oauthConfig: IEnvironmentAuthConfig,
    private _appflowLiveUpdatesService: AppflowLiveUpdatesService,
    private _storage: Storage
  ) {}

  public async init() {
    this.initVersion();
    if (this._platform.is('hybrid')) {
      await this.initMobile();
    } else {
      await this.initWeb();
    }
    this._ready.resolve();
  }

  private initVersion() {
    this._gitCommitHash = latestCommit;
    this._buildNumber = buildNumber;
  }

  private async getDeploymentOverride() {
    await this._platform.ready();
    if (this._environmentBranch) {
      return this._environmentBranch;
    }
    const branchFromStorage = await this._storage.get('BRANCH');
    if (branchFromStorage) {
      if (
        branchFromStorage === Branch.dev ||
        branchFromStorage === Branch.staging ||
        branchFromStorage === Branch.production
      ) {
        return branchFromStorage;
      }
    }
  }

  private async initMobile() {
    let deployment = await this.getDeploymentOverride();
    if (!deployment) {
      deployment = this._appflowLiveUpdatesService.currentDeployment
        ? this._appflowLiveUpdatesService.currentDeployment
        : Branch.production;
      if (deployment) {
        debug('deployment from appflow', deployment);
      }
    }
    await this.initAlbertaUrls(deployment);
  }

  private async initWeb() {
    const deployment = await this.getDeploymentOverride();
    await this.initAlbertaUrls(deployment);
  }

  private _setAlbertaUrlsFromBranch(branch: string) {
    switch (branch) {
      case Branch.dev:
        this._alberta_baseurl = this._albertaBaseUrls.dev;
        this._portalUrl = this._portalUrls.dev;
        this._signalingServerUrl = this._signalingServerUrls.dev;
        this._kimServiceUrl = this._kimServiceUrls.dev;
        this._keaServiceUrl = this._keaServiceUrls.dev;
        this._authConfig = this._oauthConfig.dev;
        break;
      case Branch.staging:
        this._alberta_baseurl = this._albertaBaseUrls.staging;
        this._portalUrl = this._portalUrls.staging;
        this._signalingServerUrl = this._signalingServerUrls.staging;
        this._kimServiceUrl = this._kimServiceUrls.staging;
        this._keaServiceUrl = this._keaServiceUrls.staging;
        this._authConfig = this._oauthConfig.staging;
        break;
      case Branch.uat:
        this._alberta_baseurl = this._albertaBaseUrls.uat;
        this._portalUrl = this._portalUrls.uat;
        this._signalingServerUrl = this._signalingServerUrls.uat;
        this._kimServiceUrl = this._kimServiceUrls.uat;
        this._keaServiceUrl = this._keaServiceUrls.uat;
        this._authConfig = this._oauthConfig.uat;
        break;
      case Branch.mdr:
        this._alberta_baseurl = this._albertaBaseUrls.mdr;
        this._portalUrl = this._portalUrls.mdr;
        this._signalingServerUrl = this._signalingServerUrls.mdr;
        this._kimServiceUrl = this._kimServiceUrls.mdr;
        this._keaServiceUrl = this._keaServiceUrls.mdr;
        this._authConfig = this._oauthConfig.mdr;
        break;
      default:
        this._alberta_baseurl = this._albertaBaseUrls.production;
        this._portalUrl = this._portalUrls.production;
        this._signalingServerUrl = this._signalingServerUrls.production;
        this._kimServiceUrl = this._kimServiceUrls.production;
        this._keaServiceUrl = this._keaServiceUrls.production;
        this._authConfig = this._oauthConfig.production;
        this._isProduction = true;
    }
  }

  private async initAlbertaUrls(deployment: string) {
    if (this._platform.is('hybrid')) {
      this._branch = deployment ? deployment : Branch.production;
      this._setAlbertaUrlsFromBranch(this._branch);
      return;
    }
    switch (this._windowHost) {
      case 'localhost':
      case '127.0.0.1':
        this._branch = deployment ? deployment : Branch.dev;
        this._setAlbertaUrlsFromBranch(this._branch);
        break;
      case `dev.app.${environment.baseUrl}`:
        this._alberta_baseurl = this._albertaBaseUrls.dev;
        this._portalUrl = this._portalUrls.dev;
        this._branch = deployment ? deployment : Branch.dev;
        this._signalingServerUrl = this._signalingServerUrls.dev;
        this._kimServiceUrl = this._kimServiceUrls.dev;
        this._keaServiceUrl = this._keaServiceUrls.dev;
        this._authConfig = this._oauthConfig.dev;
        break;
      case `staging.app.${environment.baseUrl}`:
        this._alberta_baseurl = this._albertaBaseUrls.staging;
        this._portalUrl = this._portalUrls.staging;
        this._branch = deployment ? deployment : Branch.staging;
        this._signalingServerUrl = this._signalingServerUrls.staging;
        this._kimServiceUrl = this._kimServiceUrls.staging;
        this._keaServiceUrl = this._keaServiceUrls.staging;
        this._authConfig = this._oauthConfig.staging;
        break;
      case `uat.app.${environment.baseUrl}`:
        this._alberta_baseurl = this._albertaBaseUrls.uat;
        this._portalUrl = this._portalUrls.uat;
        this._branch = deployment ? deployment : Branch.uat;
        this._signalingServerUrl = this._signalingServerUrls.uat;
        this._kimServiceUrl = this._kimServiceUrls.uat;
        this._keaServiceUrl = this._keaServiceUrls.uat;
        this._authConfig = this._oauthConfig.uat;
        break;
      case `mdr-uat.app.${environment.baseUrl}`:
        this._alberta_baseurl = this._albertaBaseUrls.uat;
        this._portalUrl = this._portalUrls.uat;
        this._branch = deployment ? deployment : Branch.uat;
        this._signalingServerUrl = this._signalingServerUrls.uat;
        this._kimServiceUrl = this._kimServiceUrls.uat;
        this._keaServiceUrl = this._keaServiceUrls.uat;
        this._authConfig = this._oauthConfig.uat;
        break;
      case `mdr.${environment.baseUrl}`:
        this._alberta_baseurl = this._albertaBaseUrls.mdr;
        this._portalUrl = this._portalUrls.mdr;
        this._branch = deployment ? deployment : Branch.mdr;
        this._signalingServerUrl = this._signalingServerUrls.mdr;
        this._kimServiceUrl = this._kimServiceUrls.mdr;
        this._keaServiceUrl = this._keaServiceUrls.mdr;
        this._authConfig = this._oauthConfig.mdr;
        break;
      default:
        if (this._windowHost?.endsWith(`.alberta.dev.apps.${environment.baseUrl}`)) {
          this._alberta_baseurl = this._albertaBaseUrls.dev;
          this._portalUrl = this._portalUrls.dev;
          this._branch = deployment ? deployment : Branch.dev;
          this._signalingServerUrl = this._signalingServerUrls.dev;
          this._kimServiceUrl = this._kimServiceUrls.dev;
          this._keaServiceUrl = this._keaServiceUrls.dev;
          this._authConfig = this._oauthConfig.dev;
        } else {
          this._alberta_baseurl = this._albertaBaseUrls.production;
          this._portalUrl = this._portalUrls.production;
          this._signalingServerUrl = this._signalingServerUrls.production;
          this._kimServiceUrl = this._kimServiceUrls.production;
          this._keaServiceUrl = this._keaServiceUrls.production;
          this._branch = deployment ? deployment : Branch.production;
          this._authConfig = this._oauthConfig.production;
          this._isProduction = true;
        }
    }
    debug('_alberta_baseurl', this.alberta_baseurl);
    debug('_authConfig', this._authConfig);
    debug('_portalUrl', this._portalUrl);
    debug('_branch', this._branch);
  }

  public isProductionBranch(): boolean {
    return this._branch === Branch.production || this._branch === Branch.mdr;
  }

  public isMdrBranch(): boolean {
    return this._branch === Branch.mdr || this._branch === Branch.uat;
  }
}
